import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Check, AlertCircle, Trash2, Sparkles, Calendar as CalendarIcon, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const Tasks = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('list'); // 'list' or 'calendar'
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', urgent: false, due_date: '' });
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks?filter=${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
    setLoading(false);
  }, [filter, token]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const addTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      await axios.post(
        `${API}/tasks`,
        { title: newTask.title, urgent: newTask.urgent, due_date: newTask.due_date || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTask({ title: '', urgent: false, due_date: '' });
      setShowAddTask(false);
      fetchTasks();
      toast.success('Task added');
    } catch (error) {
      toast.error('Could not add task');
    }
  };

  const toggleTask = async (taskId, completed) => {
    try {
      await axios.patch(
        `${API}/tasks/${taskId}`,
        { completed: !completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTasks();
    } catch (error) {
      toast.error('Could not update task');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Could not delete task');
    }
  };

  const filters = [
    { value: 'all', label: 'All Tasks' },
    { value: 'today', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
  ];

  const pendingTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  // Group Brian's tasks by week
  const brianTasks = pendingTasks.filter(t => t.source === 'brian');
  const userTasks = pendingTasks.filter(t => t.source !== 'brian');

  const weekGroups = {};
  brianTasks.forEach(t => {
    const week = t.week || 1;
    if (!weekGroups[week]) weekGroups[week] = [];
    weekGroups[week].push(t);
  });
  const sortedWeeks = Object.keys(weekGroups).sort((a, b) => a - b);

  // Calendar helpers
  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days = [];
    // Previous month padding
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    // Current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    // Next month padding
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  };

  const getTasksForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return tasks.filter(t => t.due_date === dateStr);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const prevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const selectedDateStr = selectedDate ? selectedDate.toISOString().split('T')[0] : null;
  const selectedTasks = selectedDate ? getTasksForDate(selectedDate) : [];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl headline text-black mb-2" data-testid="tasks-heading">Tasks</h1>
          <p className="text-black/60 body-text">
            Track your progress and stay on top of your to-dos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex bg-[#F0F0F0] rounded-none p-1" data-testid="view-toggle">
            <button
              onClick={() => setView('list')}
              className={`p-2 rounded-none transition-all ${view === 'list' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
              data-testid="list-view-btn"
              title="List view"
            >
              <List className="h-4 w-4 text-black/70" />
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`p-2 rounded-none transition-all ${view === 'calendar' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
              data-testid="calendar-view-btn"
              title="Calendar view"
            >
              <CalendarIcon className="h-4 w-4 text-black/70" />
            </button>
          </div>
          <Button
            onClick={() => setShowAddTask(true)}
            className="bg-primary text-white hover:bg-primary/90 rounded-none"
            data-testid="add-task-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Add Task Form */}
      {showAddTask && (
        <div className="bg-white rounded-none p-4 border border-border">
          <div className="flex gap-3">
            <Input
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              placeholder="What needs to be done?"
              className="flex-1 bg-secondary border-0 rounded-none"
              onKeyDown={(e) => e.key === 'Enter' && addTask()}
              autoFocus
              data-testid="new-task-input"
            />
            <Button onClick={addTask} className="bg-primary text-white hover:bg-primary/90 rounded-none">Add</Button>
            <Button variant="ghost" onClick={() => setShowAddTask(false)}>Cancel</Button>
          </div>
          <div className="flex items-center gap-4 mt-3">
            <label className="flex items-center gap-2 text-sm text-black/60">
              <input
                type="checkbox"
                checked={newTask.urgent}
                onChange={(e) => setNewTask({ ...newTask, urgent: e.target.checked })}
                className="rounded"
              />
              Mark as urgent
            </label>
            <label className="flex items-center gap-2 text-sm text-black/60">
              <CalendarIcon className="h-3.5 w-3.5" />
              <input
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                className="bg-secondary border-0 rounded-none px-2 py-1 text-sm"
                data-testid="new-task-date"
              />
            </label>
          </div>
        </div>
      )}

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="bg-white rounded-none border border-border overflow-hidden" data-testid="calendar-view">
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <button onClick={prevMonth} className="p-2 hover:bg-secondary rounded-none">
              <ChevronLeft className="h-5 w-5 text-black/60" />
            </button>
            <h2 className="subhead text-black text-lg">
              {MONTHS[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
            </h2>
            <button onClick={nextMonth} className="p-2 hover:bg-secondary rounded-none">
              <ChevronRight className="h-5 w-5 text-black/60" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-border">
            {DAYS.map(d => (
              <div key={d} className="text-center py-2 text-xs font-medium text-black/40 uppercase tracking-wider">{d}</div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {getCalendarDays().map((day, idx) => {
              const dayTasks = getTasksForDate(day.date);
              const hasTasks = dayTasks.length > 0;
              const hasUrgent = dayTasks.some(t => t.urgent && !t.completed);
              const allComplete = hasTasks && dayTasks.every(t => t.completed);
              const isSelected = selectedDateStr === day.date.toISOString().split('T')[0];

              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDate(day.date)}
                  className={`
                    relative min-h-[72px] p-2 border-b border-r border-[#F0F0F0] text-left transition-all
                    ${!day.isCurrentMonth ? 'bg-[#FAFAFA] opacity-40' : 'hover:bg-[#FFFEF5]'}
                    ${isSelected ? 'bg-[#F6ADBC]/10 ring-2 ring-[#F6ADBC] ring-inset' : ''}
                    ${isToday(day.date) ? 'bg-[#F6ADBC]/5' : ''}
                  `}
                  data-testid={`calendar-day-${day.date.toISOString().split('T')[0]}`}
                >
                  <span className={`
                    text-sm font-medium
                    ${isToday(day.date) ? 'bg-[#F6ADBC] text-[#1E491A] w-6 h-6 rounded-none flex items-center justify-center' : ''}
                    ${!day.isCurrentMonth ? 'text-black/30' : 'text-black'}
                  `}>
                    {day.date.getDate()}
                  </span>
                  {hasTasks && (
                    <div className="mt-1 flex gap-1 flex-wrap">
                      {dayTasks.slice(0, 3).map((t, i) => (
                        <div
                          key={i}
                          className={`w-1.5 h-1.5 rounded-none ${
                            t.completed ? 'bg-[#F6ADBC]' :
                            t.urgent ? 'bg-red-400' :
                            'bg-[#F6ADBC]'
                          }`}
                        />
                      ))}
                      {dayTasks.length > 3 && (
                        <span className="text-[10px] text-black/40">+{dayTasks.length - 3}</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Selected Date Tasks */}
          {selectedDate && (
            <div className="border-t border-border p-4" data-testid="selected-date-tasks">
              <h3 className="subhead text-black mb-3">
                {selectedDate.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' })}
                <span className="text-black/40 font-normal ml-2">
                  {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''}
                </span>
              </h3>
              {selectedTasks.length > 0 ? (
                <div className="space-y-2">
                  {selectedTasks.map(task => (
                    <div key={task.id} className="flex items-center gap-3 p-2 rounded-none hover:bg-secondary/30">
                      <button
                        onClick={() => toggleTask(task.id, task.completed)}
                        className={`w-5 h-5 rounded-none border-2 flex-shrink-0 flex items-center justify-center ${
                          task.completed ? 'bg-[rgba(255,192,0,0.1)]0 border-[#F6ADBC]' : 'border-border hover:border-primary'
                        }`}
                      >
                        {task.completed && <Check className="h-3 w-3 text-white" />}
                      </button>
                      <span className={`flex-1 text-sm ${task.completed ? 'line-through text-black/40' : 'text-black'}`}>
                        {task.title}
                      </span>
                      {task.urgent && !task.completed && (
                        <AlertCircle className="h-3.5 w-3.5 text-orange-500" />
                      )}
                      {task.source === 'brian' && (
                        <Sparkles className="h-3.5 w-3.5 text-primary" />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-black/40">No tasks scheduled for this day.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <>
          {/* Filters */}
          <div className="flex gap-2">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-4 py-2 rounded-none text-sm transition-all ${
                  filter === f.value
                    ? 'bg-primary text-white subhead'
                    : 'bg-secondary text-black/60 hover:bg-secondary/80'
                }`}
                data-testid={`filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Task List */}
          {loading ? (
            <div className="text-center py-8 text-black/40">Loading...</div>
          ) : (
            <div className="space-y-6">
              {/* Brian's Action Plan */}
              {filter !== 'completed' && sortedWeeks.length > 0 && (
                <div className="space-y-4" data-testid="brian-action-plan">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h2 className="subhead text-black">Brian's Action Plan</h2>
                  </div>
                  {sortedWeeks.map((week) => (
                    <div key={week} className="bg-white rounded-none border border-border overflow-hidden">
                      <div className="px-5 py-3 border-b border-border bg-primary/5 flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-primary" />
                        <h3 className="text-sm subhead text-black">Week {week}</h3>
                        <span className="text-xs text-black/40 ml-auto">{weekGroups[week].length} tasks</span>
                      </div>
                      <div className="divide-y divide-border">
                        {weekGroups[week].map((task) => (
                          <TaskRow key={task.id} task={task} onToggle={toggleTask} onDelete={deleteTask} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* User's Own Tasks */}
              {filter !== 'completed' && userTasks.length > 0 && (
                <div className="bg-white rounded-none border border-border overflow-hidden">
                  <div className="px-5 py-3 border-b border-border">
                    <h2 className="subhead text-black">Your Tasks ({userTasks.length})</h2>
                  </div>
                  <div className="divide-y divide-border">
                    {userTasks.map((task) => (
                      <TaskRow key={task.id} task={task} onToggle={toggleTask} onDelete={deleteTask} />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Tasks */}
              {filter !== 'today' && completedTasks.length > 0 && (
                <div className="bg-white rounded-none border border-border overflow-hidden">
                  <div className="px-5 py-3 border-b border-border">
                    <h2 className="subhead text-black/60">Completed ({completedTasks.length})</h2>
                  </div>
                  <div className="divide-y divide-border">
                    {completedTasks.map((task) => (
                      <div key={task.id} className="flex items-center gap-4 p-4 opacity-60">
                        <button
                          onClick={() => toggleTask(task.id, task.completed)}
                          className="w-6 h-6 rounded-none bg-[rgba(255,192,0,0.1)]0 flex items-center justify-center flex-shrink-0"
                        >
                          <Check className="h-4 w-4 text-white" />
                        </button>
                        <p className="flex-1 text-black/60 line-through">{task.title}</p>
                        <button onClick={() => deleteTask(task.id)} className="p-2 text-black/30 hover:text-red-500 rounded-none">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {tasks.length === 0 && (
                <div className="bg-white rounded-none p-8 border border-border text-center">
                  <p className="text-black/40">No tasks yet. Complete a stage to get Brian's personalised action plan!</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TaskRow = ({ task, onToggle, onDelete }) => (
  <div className="flex items-center gap-4 p-4 hover:bg-secondary/30 transition-colors">
    <button
      onClick={() => onToggle(task.id, task.completed)}
      className="w-6 h-6 rounded-none border-2 border-border hover:border-primary flex items-center justify-center flex-shrink-0"
      data-testid={`toggle-task-${task.id}`}
    />
    <div className="flex-1 min-w-0">
      <p className="text-black">{task.title}</p>
      <div className="flex items-center gap-3 mt-1">
        {task.urgent && (
          <span className="text-xs text-orange-600 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Urgent
          </span>
        )}
        {task.due_date && (
          <span className="text-xs text-black/40 flex items-center gap-1">
            <CalendarIcon className="h-3 w-3" />
            {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
          </span>
        )}
        {task.stage && (
          <span className="text-xs text-black/40">Stage {task.stage.replace('stage', '')}</span>
        )}
        {task.source === 'brian' && (
          <span className="text-xs bg-primary/20 text-black/60 px-2 py-0.5 rounded-none flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Brian
          </span>
        )}
      </div>
    </div>
    <button onClick={() => onDelete(task.id)} className="p-2 text-black/30 hover:text-red-500 rounded-none">
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

export default Tasks;
